import React, { useEffect, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Card } from '@abyss/web/ui/Card';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { DataTable } from '@abyss/web/ui/DataTable';
import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { BreadcrumbItem } from '@src/api/common/BreadcrumbTypes';
import { ModalService } from '@src/api/common/ModalService';
import { API_URL } from '@src/constants/BaseUrls';
import { CookieService } from '@src/api/common/CookieService';

const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) {
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveRouteToolApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return 'http://localhost:5000';
  }
  return configured;
};

const buildApiUrl = (baseUrl: string, path: string) => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

const getManageEntityTab = (search: string): 'add' | 'history' => {
  try {
    const params = new URLSearchParams(search || '');
    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'remove' || tab === 'history') {
      return 'history';
    }
  } catch {
    return 'add';
  }
  return 'add';
};

interface EntityOption {
  value: string;
  label: string;
}

interface EntityRow {
  entityName: string;
  createdBy: string;
  createdDate: string;
}

interface RemovedHistoryRow {
  entityName: string;
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
}

export const ManageEntityScreen = () => {
  const { getLocation } = useRouter();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const location = getLocation();
  const baseUrl = resolveRouteToolApiBase();
  const { showModal, hideModal } = ModalService();
  const activeTab = getManageEntityTab(location?.search || '');
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [entityRows, setEntityRows] = useState<EntityRow[]>([]);
  const [removedHistoryRows, setRemovedHistoryRows] = useState<RemovedHistoryRow[]>([]);
  const [entitiesError, setEntitiesError] = useState('');
  const [removedHistoryError, setRemovedHistoryError] = useState('');
  const [newEntityName, setNewEntityName] = useState('');
  const [addEntityError, setAddEntityError] = useState('');
  const [addEntityLoading, setAddEntityLoading] = useState(false);
  const [addEntitySuccess, setAddEntitySuccess] = useState('');
  const [removeEntityError, setRemoveEntityError] = useState('');
  const [removeEntityLoading, setRemoveEntityLoading] = useState('');

  const getAuthHeaders = (): HeadersInit => {
    const token = CookieService.GetCookie('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadEntities = async () => {
    setEntitiesError('');
    setRemovedHistoryError('');
    try {
      const [entitiesResponse, summaryResponse, removedHistoryResponse] = await Promise.all([
        fetch(buildApiUrl(baseUrl, '/api/entities'), {
          headers: getAuthHeaders(),
        }),
        fetch(buildApiUrl(baseUrl, '/api/entities/summary'), {
          headers: getAuthHeaders(),
        }),
        fetch(buildApiUrl(baseUrl, '/api/entities/removed-history'), {
          headers: getAuthHeaders(),
        }),
      ]);

      if (!entitiesResponse.ok) {
        throw new Error(`Unable to load entities (${entitiesResponse.status})`);
      }

      const data = await entitiesResponse.json();
      const names = Array.isArray(data?.entities) ? data.entities : [];

      let summaryByEntity: Record<string, any> = {};
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json().catch(() => ({}));
        const summaryList = Array.isArray(summaryData?.entities) ? summaryData.entities : [];
        summaryByEntity = summaryList.reduce((acc: Record<string, any>, item: any) => {
          const name = String(item?.entity || '').trim();
          if (name) {
            acc[name.toLowerCase()] = item;
          }
          return acc;
        }, {});
      }

      const options = names
        .map((name: string) => ({ value: name, label: name }))
        .sort((a: EntityOption, b: EntityOption) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );

      const rows: EntityRow[] = options.map((option) => {
        const summary = summaryByEntity[option.value.toLowerCase()] || {};
        const createdBy =
          summary.created_by ||
          summary.createdBy ||
          summary.uploaded_by ||
          summary.uploadedBy ||
          summary.username ||
          '--';
        const createdDateRaw =
          summary.created_date ||
          summary.createdDate ||
          summary.last_modified ||
          summary.lastModified ||
          '';

        const formattedDate = createdDateRaw
          ? new Date(createdDateRaw).toLocaleString()
          : '--';

        return {
          entityName: option.value,
          createdBy: String(createdBy || '--'),
          createdDate:
            formattedDate && formattedDate !== 'Invalid Date' ? formattedDate : '--',
        };
      });

      let removedRows: RemovedHistoryRow[] = [];
      if (removedHistoryResponse.ok) {
        const removedData = await removedHistoryResponse.json().catch(() => ({}));
        const removedList = Array.isArray(removedData?.entities) ? removedData.entities : [];
        removedRows = removedList.map((item: any) => {
          const createdOnRaw = item?.created_on || item?.createdOn || '';
          const updatedOnRaw = item?.updated_on || item?.updatedOn || '';
          const createdOnFormatted = createdOnRaw
            ? new Date(createdOnRaw).toLocaleString()
            : '--';
          const updatedOnFormatted = updatedOnRaw
            ? new Date(updatedOnRaw).toLocaleString()
            : '--';

          return {
            entityName: String(item?.entity_name || item?.entityName || '--'),
            createdBy: String(item?.created_by || item?.createdBy || '--'),
            createdOn:
              createdOnFormatted && createdOnFormatted !== 'Invalid Date'
                ? createdOnFormatted
                : '--',
            updatedBy: String(item?.updated_by || item?.updatedBy || '--'),
            updatedOn:
              updatedOnFormatted && updatedOnFormatted !== 'Invalid Date'
                ? updatedOnFormatted
                : '--',
          };
        });
      } else {
        setRemovedHistoryError(`Unable to load removed history (${removedHistoryResponse.status}).`);
      }

      setEntityOptions(options);
      setEntityRows(rows);
      setRemovedHistoryRows(removedRows);
    } catch (error: any) {
      setEntitiesError(error?.message || 'Unable to load entities.');
      setEntityOptions([]);
      setEntityRows([]);
      setRemovedHistoryRows([]);
    }
  };

  useEffect(() => {
    const breadcrumbs: Array<BreadcrumbItem> = [
      { title: 'Home', isLink: true, href: '/' },
      { title: 'Route Tool', isLink: false },
      {
        title: activeTab === 'history' ? 'Removed History' : 'Add/Remove Entity',
        isLink: false,
      },
    ];

    void (async () => {
      // Clear any stale header text (for example from Download Templates) and set screen-specific breadcrumb.
      await UpdatePageTitle({ title: '' });
      await UpdateBreadcrumb(breadcrumbs);
    })();
  }, [activeTab]);

  useEffect(() => {
    void loadEntities();
  }, []);

  const addEntity = async () => {
    const name = newEntityName.trim();
    if (!name) {
      setAddEntityError('Entity name is required.');
      setAddEntitySuccess('');
      return;
    }

    const alreadyExists = entityOptions.some(
      (opt) => opt.value.toLowerCase() === name.toLowerCase()
    );
    if (alreadyExists) {
      setAddEntityError(`Entity "${name}" already exists.`);
      setAddEntitySuccess('');
      return;
    }

    setAddEntityLoading(true);
    setAddEntityError('');
    setAddEntitySuccess('');
    try {
      const response = await fetch(buildApiUrl(baseUrl, '/api/entities'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ entity_name: name }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAddEntityError(result?.message || result?.error || `Failed with ${response.status}`);
        return;
      }
      await loadEntities();
      setAddEntitySuccess(`Entity "${name}" added successfully.`);
      setNewEntityName('');
    } catch (error: any) {
      setAddEntityError(error?.message || 'Unable to add entity. Please try again.');
    } finally {
      setAddEntityLoading(false);
    }
  };

  const removeEntity = async (entityName: string) => {
    setRemoveEntityError('');
    setAddEntitySuccess('');
    setRemoveEntityLoading(entityName);
    try {
      const response = await fetch(
        buildApiUrl(baseUrl, `/api/entities/${encodeURIComponent(entityName)}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setRemoveEntityError(result?.message || result?.error || `Failed with ${response.status}`);
        return;
      }
      setAddEntitySuccess(`Entity "${entityName}" removed successfully.`);
      await loadEntities();
    } catch (error: any) {
      setRemoveEntityError(error?.message || 'Unable to remove entity. Please try again.');
    } finally {
      setRemoveEntityLoading('');
    }
  };

  const confirmRemoveEntity = async (entityName: string) => {
    await showModal({
      key: 'remove-entity-modal',
      title: 'Remove Entity',
      message: `Are you sure you want to remove "${entityName}"?`,
      handleConfirm: async () => {
        await removeEntity(entityName);
      },
      handleCancel: async () => {
        await hideModal('remove-entity-modal');
      },
      cancelText: 'Cancel',
      submitText: 'OK',
    });
  };

  const tableColumns = [
    { Header: 'Entity Name', accessor: 'entityName' },
    { Header: 'Created By', accessor: 'createdBy' },
    { Header: 'Created Date', accessor: 'createdDate' },
    // {  /* Action column — hidden; uncomment to re-enable */
    //   Header: 'Action',
    //   accessor: '__actions',
    //   disableSortBy: true,
    //   Cell: ({ row }: { row: { original: EntityRow } }) => (
    //     <Button
    //       size="$sm"
    //       variant="destructive"
    //       isDisabled={removeEntityLoading === row.original.entityName}
    //       isLoading={removeEntityLoading === row.original.entityName}
    //       ariaLoadingLabel="Removing entity..."
    //       onClick={() => void confirmRemoveEntity(row.original.entityName)}
    //     >
    //       Remove
    //     </Button>
    //   ),
    // },
  ];

  const removedHistoryColumns = [
    { Header: 'Entity Name', accessor: 'entityName' },
    { Header: 'Created By', accessor: 'createdBy' },
    { Header: 'Created On', accessor: 'createdOn' },
    { Header: 'Updated By', accessor: 'updatedBy' },
    { Header: 'Updated On', accessor: 'updatedOn' },
  ];

  const tableState = useDataTable({
    initialData: entityRows,
    initialColumns: tableColumns,
    uniqueStorageId: 'route-tool-manage-entity-grid',
    showPagination: true,
    showGlobalFilter: false,
    showFilterDataset: false,
    showTableSettings: false,
    showTopPagination: true,
    showBottomPagination: true,
  });

  const removedHistoryTableState = useDataTable({
    initialData: removedHistoryRows,
    initialColumns: removedHistoryColumns,
    uniqueStorageId: 'route-tool-removed-history-grid',
    showPagination: true,
    showGlobalFilter: false,
    showFilterDataset: false,
    showTableSettings: false,
    showTopPagination: true,
    showBottomPagination: true,
  });

  useEffect(() => {
    tableState.setData(entityRows);
  }, [entityRows]);

  useEffect(() => {
    removedHistoryTableState.setData(removedHistoryRows);
  }, [removedHistoryRows]);

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <Card>
        <Card.Section css={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Label size="lg" fontWeight="bold" color="$primary1">
            {activeTab === 'history' ? 'Removed History' : 'Add/Remove Entity'}
          </Label>

          {activeTab === 'add' ? (
            <>
              <Label size="sm" color="$gray7" css={{ marginBottom: '-8px' }}>
                Acquired Entity Name <span style={{ color: '#d92d20' }}>*</span>
              </Label>

              <input
                type="text"
                value={newEntityName}
                onChange={(e) => {
                  setNewEntityName(e.target.value);
                  setAddEntityError('');
                  setAddEntitySuccess('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addEntity();
                  }
                }}
                placeholder="Enter acquired entity name"
                style={{
                  width: '100%',
                  height: '40px',
                  border: `1px solid ${addEntityError ? '#d92d20' : '#d3dce8'}`,
                  borderRadius: '4px',
                  padding: '0 12px',
                  fontSize: '14px',
                  backgroundColor: '#fff',
                  boxSizing: 'border-box',
                }}
              />

              <Layout.Group css={{ justifyContent: 'flex-end' }}>
                <Button
                  size="$sm"
                  isDisabled={addEntityLoading}
                  isLoading={addEntityLoading}
                  ariaLoadingLabel="Adding entity..."
                  onClick={() => void addEntity()}
                >
                  <IconSymbol icon="add" size={14} />
                  Add Entity
                </Button>
              </Layout.Group>
            </>
          ) : null}

          {addEntityError ? (
            <Label size="sm" color="$error1">
              {addEntityError}
            </Label>
          ) : null}

          {addEntitySuccess ? (
            <Label size="sm" color="$success1">
              {addEntitySuccess}
            </Label>
          ) : null}

          {entitiesError ? (
            <Label size="sm" color="$error1">
              {entitiesError}
            </Label>
          ) : null}

          {removeEntityError ? (
            <Label size="sm" color="$error1">
              {removeEntityError}
            </Label>
          ) : null}

          {activeTab === 'add' ? (
            entityRows.length ? (
              <DataTable title="Manage Entities" hideTitleHeader tableState={tableState} />
            ) : (
              <Label size="sm" color="$gray6">No entities found.</Label>
            )
          ) : null}

          {removedHistoryError ? (
            <Label size="sm" color="$error1">
              {removedHistoryError}
            </Label>
          ) : null}

          {activeTab === 'history' ? (
            removedHistoryRows.length ? (
              <DataTable title="Removed History" hideTitleHeader tableState={removedHistoryTableState} />
            ) : (
              <Label size="sm" color="$gray6">No removed history found.</Label>
            )
          ) : null}
        </Card.Section>
      </Card>
    </Box>
  );
};
